import { getBlockedByCategoryUrl } from '../chrome/routing';
import { redirectToURL } from '../actions/navigationActions';
import { getRemoteBlockerBaseUrl } from './remoteBlockerUrl';

export type BlockerPageExtraParams = Record<
  string,
  string | number | ReadonlyArray<string | number> | undefined
>;

const appendExtraParams = (
  params: URLSearchParams,
  extraParams: BlockerPageExtraParams
): void => {
  for (const [key, value] of Object.entries(extraParams)) {
    if (value === undefined || value === null) {
      continue;
    }
    const stringValue = Array.isArray(value) ? value.join(',') : String(value);
    if (stringValue === '') {
      continue;
    }
    params.set(key, stringValue);
  }
};

// Blocker pages split array params before decoding; keep commas literal.
const restoreLiteralCommas = (url: string): string => url.replace(/%2C/g, ',');

export const parseBlockerPageUrl = (
  blockerPageUrl: string,
  sourceBlockedUrl: string,
  extraParams: BlockerPageExtraParams = {}
): string => {
  const url = new URL(blockerPageUrl);
  url.searchParams.set('u', btoa(sourceBlockedUrl));
  appendExtraParams(url.searchParams, extraParams);
  return restoreLiteralCommas(url.toString());
};

export const isBlockerPage = (url: string): boolean => {
  const remoteBlockerBaseUrl = getRemoteBlockerBaseUrl();
  return remoteBlockerBaseUrl !== '' && url.startsWith(remoteBlockerBaseUrl);
};

// Chrome exposes `initiator` as an origin, so compare origins only.
export const isBlockerPageInitiator = (
  initiator: string | undefined
): boolean => {
  if (!initiator) {
    return false;
  }

  const remoteBlockerBaseUrl = getRemoteBlockerBaseUrl();
  if (remoteBlockerBaseUrl === '') {
    return false;
  }

  try {
    return new URL(initiator).origin === new URL(remoteBlockerBaseUrl).origin;
  } catch {
    return false;
  }
};

/**
 * Extracts the original URL from a blocker page URL by decoding the 'u' parameter.
 *
 * @param url - The blocker page URL containing the encoded original URL
 * @returns The decoded original URL, or null if extraction fails
 */
export const extractOriginalUrlFromBlockerPage = (
  url: string
): string | null => {
  try {
    const urlObj = new URL(url);
    const encodedUrl = urlObj.searchParams.get('u');

    if (encodedUrl) {
      const decodedUrl = atob(encodedUrl);
      return decodedUrl;
    }

    return null;
  } catch (error) {
    console.warn('Failed to extract original URL from blocker page:', error);
    return null;
  }
};

export const safeHostname = (url: string): string | undefined => {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
};

export const redirectToBlockedByCategoryScreen = (
  tabId: number,
  blockedUrl?: string,
  categoriesMatch?: ReadonlyArray<number>
): Promise<chrome.tabs.Tab | void> => {
  const blockerUrl = getBlockedByCategoryUrl();
  if (!blockedUrl) {
    return redirectToURL(tabId, blockerUrl);
  }

  const extraParams: BlockerPageExtraParams = {
    domain: safeHostname(blockedUrl),
    categories: categoriesMatch,
  };
  const finalUrl = parseBlockerPageUrl(blockerUrl, blockedUrl, extraParams);
  return redirectToURL(tabId, finalUrl);
};
